import { useMemo, useState, type FormEvent } from "react";
import {
  Check,
  FilePlus2,
  FileText,
  ListPlus,
  LoaderCircle,
  Pencil,
  Plus,
  WandSparkles,
} from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import {
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import type { BoardItem, IdeaInput } from "../types";
import { calculateWordTargets, formatWordRange } from "../utils/wordTargets";

export interface BulkIdeaParseResult {
  items: IdeaInput[];
  errors: Array<{ line: number; message: string }>;
}

export function parseSuggestedDuration(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+$/u.test(trimmed)) throw new Error("Duration must be a whole number from 1 to 180.");
  const duration = Number(trimmed);
  if (duration < 1 || duration > 180) throw new Error("Duration must be from 1 to 180 minutes.");
  return duration;
}

export function parseBulkIdeas(channelId: string, value: string): BulkIdeaParseResult {
  const items: IdeaInput[] = [];
  const errors: BulkIdeaParseResult["errors"] = [];
  for (const [index, rawLine] of value.split(/\r?\n/).entries()) {
    const line = rawLine.trim();
    if (!line) continue;
    const parts = (line.includes("\t") ? line.split("\t") : line.split(/\s*\|\s*/u))
      .map((part) => part.trim());
    const title = parts.shift() ?? "";
    if (!title) {
      errors.push({ line: index + 1, message: "Title is required." });
      continue;
    }
    if (parts.length > 2) {
      errors.push({
        line: index + 1,
        message: "Use Title | Context | minutes, with no extra columns.",
      });
      continue;
    }
    let suggestedDurationMinutes: number | null = null;
    const durationPart = parts.length === 2
      ? parts.pop() ?? ""
      : (/^[+-]?\d+(?:\.\d+)?$/u.test(parts[0] ?? "") ? parts.pop() ?? "" : null);
    if (durationPart !== null && durationPart !== "") {
      try {
        suggestedDurationMinutes = parseSuggestedDuration(durationPart);
      } catch (error) {
        errors.push({
          line: index + 1,
          message: error instanceof Error ? error.message : "Duration is invalid.",
        });
        continue;
      }
    }
    const context = parts.join(" | ").trim();
    items.push({
      channelId,
      title,
      ...(context ? { context } : {}),
      ...(suggestedDurationMinutes === null ? {} : { suggestedDurationMinutes }),
    });
  }
  return { items, errors };
}

export function IdeasPage({ client = portalApi }: { client?: PortalApiClient }) {
  const { session } = useAuth();
  const data = useAsyncData(async () => {
    const [board, catalog] = await Promise.all([client.board.get(), client.catalog.get()]);
    return {
      ideas: board.items.filter((item) => item.type === "idea" && item.status === "ideas"),
      catalog,
    };
  }, [client]);
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [channelId, setChannelId] = useState("");
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [suggestedDuration, setSuggestedDuration] = useState("");
  const [scriptMode, setScriptMode] = useState<"generate" | "existing">("generate");
  const [existingScript, setExistingScript] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [editing, setEditing] = useState<BoardItem | null>(null);
  const [approving, setApproving] = useState<BoardItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const bulkIdeas = useMemo(() => parseBulkIdeas(channelId, bulkText), [bulkText, channelId]);
  const bulkSubmitLabel = bulkIdeas.items.length === 0
    ? "Add ideas"
    : `Add ${bulkIdeas.items.length} ${bulkIdeas.items.length === 1 ? "idea" : "ideas"}`;
  const canApprove = session?.user.role === "owner" || session?.user.role === "manager";

  async function submit(event: FormEvent) {
    event.preventDefault();
    let suggestedDurationMinutes: number | null = null;
    try {
      suggestedDurationMinutes = mode === "single" ? parseSuggestedDuration(suggestedDuration) : null;
    } catch (durationError) {
      setFormError(durationError instanceof Error ? durationError.message : "Duration is invalid.");
      return;
    }
    if (mode === "single" && scriptMode === "existing" && !existingScript.trim()) {
      setFormError("Enter the existing script or choose Generate script.");
      return;
    }
    const single: IdeaInput = {
      channelId,
      title: title.trim(),
      ...(context.trim() ? { context: context.trim() } : {}),
      ...(suggestedDurationMinutes === null ? {} : { suggestedDurationMinutes }),
      ...(scriptMode === "existing" ? { existingScript: existingScript.trim() } : {}),
    };
    const payload = mode === "single" ? [single] : bulkIdeas.items;
    if (mode === "bulk" && bulkIdeas.errors.length) {
      setFormError(`Fix the bulk entry errors before saving (${bulkIdeas.errors.length}).`);
      return;
    }
    if (!channelId || payload.length === 0 || payload.some((idea) => !idea.title)) {
      setFormError("Choose a channel and enter at least one title.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    setSavedMessage(null);
    try {
      if (mode === "single") {
        await client.ideas.create(single);
        setSavedMessage("Idea saved.");
      } else {
        const result = await client.ideas.bulk(payload);
        setSavedMessage(
          `${result.created.length} saved${result.duplicates.length ? `, ${result.duplicates.length} skipped as duplicates` : ""}.`,
        );
      }
      setTitle("");
      setContext("");
      setSuggestedDuration("");
      setScriptMode("generate");
      setExistingScript("");
      setBulkText("");
      await data.reload();
    } catch (requestError) {
      setFormError(requestError instanceof Error ? requestError.message : "The ideas could not be saved.");
    } finally {
      setSubmitting(false);
    }
  }

  if (data.loading) return <LoadingState label="Loading ideas" />;
  if (data.error || !data.data) {
    return <ErrorState message={data.error ?? "Ideas are unavailable."} onRetry={data.reload} />;
  }

  return (
    <>
      <PageHeader
        title="Ideas"
        description="Add one idea or paste a list. A channel and title are required; context is optional."
      />
      <section className="split-layout idea-layout">
        <div className="form-panel">
          <div className="segmented-control" aria-label="Idea entry mode">
            <button type="button" className={mode === "single" ? "active" : ""} onClick={() => setMode("single")}>
              <FilePlus2 size={16} />
              One idea
            </button>
            <button type="button" className={mode === "bulk" ? "active" : ""} onClick={() => setMode("bulk")}>
              <ListPlus size={16} />
              Bulk paste
            </button>
          </div>
          <form onSubmit={submit}>
            <Field label="Channel">
              <select required value={channelId} onChange={(event) => setChannelId(event.target.value)}>
                <option value="">Choose a channel</option>
                {data.data.catalog.channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                ))}
              </select>
            </Field>
            {mode === "single" ? (
              <>
                <Field label="Title">
                  <input
                    required
                    maxLength={240}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Video title"
                  />
                </Field>
                <Field label="Context" hint="Optional">
                  <textarea
                    rows={6}
                    value={context}
                    onChange={(event) => setContext(event.target.value)}
                    placeholder="Facts, angle, source notes, or what the video should focus on"
                  />
                </Field>
                <Field label="Suggested duration" hint="Optional. The manager can accept or replace it.">
                  <input
                    type="number"
                    min={1}
                    max={180}
                    step={1}
                    value={suggestedDuration}
                    onChange={(event) => setSuggestedDuration(event.target.value)}
                  />
                </Field>
                <div className="field">
                  <span className="field-label">Script</span>
                  <div className="segmented-control" aria-label="Script mode">
                    <button type="button" className={scriptMode === "generate" ? "active" : ""} onClick={() => setScriptMode("generate")}>
                      <WandSparkles size={16} />
                      Generate script
                    </button>
                    <button type="button" className={scriptMode === "existing" ? "active" : ""} onClick={() => setScriptMode("existing")}>
                      <FileText size={16} />
                      Use existing script
                    </button>
                  </div>
                </div>
                {scriptMode === "existing" ? (
                  <Field label="Existing script">
                    <textarea
                      required
                      rows={8}
                      value={existingScript}
                      onChange={(event) => setExistingScript(event.target.value)}
                    />
                  </Field>
                ) : null}
              </>
            ) : (
              <Field
                label="Ideas"
                hint="Title | optional context | optional suggested minutes. Tabs also work."
              >
                <textarea
                  rows={12}
                  value={bulkText}
                  onChange={(event) => setBulkText(event.target.value)}
                  placeholder={"First video title\nSecond video title | Optional context | 12"}
                />
              </Field>
            )}
            {mode === "bulk" && bulkIdeas.errors.length ? (
              <div className="form-alert" role="alert">
                {bulkIdeas.errors.map((entry) => (
                  <p key={`${entry.line}-${entry.message}`}>Line {entry.line}: {entry.message}</p>
                ))}
              </div>
            ) : null}
            {formError ? <p className="form-alert" role="alert">{formError}</p> : null}
            {savedMessage ? <p className="form-success" role="status">{savedMessage}</p> : null}
            <button
              className="button primary"
              type="submit"
              disabled={submitting || (mode === "bulk" && bulkIdeas.errors.length > 0)}
            >
              {submitting ? <LoaderCircle className="spin" size={17} /> : <Plus size={17} />}
              {mode === "bulk" ? bulkSubmitLabel : "Add idea"}
            </button>
          </form>
        </div>
        <div className="content-section flush">
          <div className="section-heading">
            <div>
              <h2>Current ideas</h2>
              <p>Ideas remain editable until a manager approves them.</p>
            </div>
            <span className="count-label">{data.data.ideas.length}</span>
          </div>
          {data.data.ideas.length ? (
            <div className="idea-list">
              {data.data.ideas.map((idea) => (
                <article key={idea.id} className="idea-row">
                  <div className="idea-row-main">
                    <strong>{idea.title}</strong>
                    <span>{idea.channelName}</span>
                    {idea.suggestedDurationMinutes ? (
                      <span>Suggested duration: {idea.suggestedDurationMinutes} minutes</span>
                    ) : null}
                    {idea.context ? <p>{idea.context}</p> : null}
                  </div>
                  <div className="row-actions">
                    <StatusBadge status={idea.status} />
                    <button className="icon-button" type="button" title="Edit idea" aria-label={`Edit ${idea.title}`} onClick={() => setEditing(idea)}>
                      <Pencil size={16} />
                    </button>
                    {canApprove ? (
                      <button className="button secondary small" type="button" onClick={() => setApproving(idea)}>
                        <Check size={15} />
                        Approve
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState title="No ideas yet" message="New ideas will appear here." />
          )}
        </div>
      </section>
      {editing ? (
        <EditIdeaDialog
          idea={editing}
          channels={data.data.catalog.channels}
          client={client}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void data.reload();
          }}
        />
      ) : null}
      {approving ? (
        <ApproveIdeaDialog
          idea={approving}
          catalog={data.data.catalog}
          client={client}
          allowNoPublicationFixture={session?.user.role === "owner"}
          onClose={() => setApproving(null)}
          onApproved={() => {
            setApproving(null);
            void data.reload();
          }}
        />
      ) : null}
    </>
  );
}

function EditIdeaDialog({
  idea,
  channels,
  client,
  onClose,
  onSaved,
}: {
  idea: BoardItem;
  channels: Array<{ id: string; name: string }>;
  client: PortalApiClient;
  onClose(): void;
  onSaved(): void;
}) {
  const [channelId, setChannelId] = useState(idea.channelId);
  const [title, setTitle] = useState(idea.title);
  const [context, setContext] = useState(idea.context ?? "");
  const [suggestedDuration, setSuggestedDuration] = useState(
    idea.suggestedDurationMinutes?.toString() ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    let suggestedDurationMinutes: number | null;
    try {
      suggestedDurationMinutes = parseSuggestedDuration(suggestedDuration);
    } catch (durationError) {
      setError(durationError instanceof Error ? durationError.message : "Duration is invalid.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await client.ideas.update(idea.id, {
        channelId,
        title: title.trim(),
        context: context.trim(),
        suggestedDurationMinutes,
      });
      onSaved();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The idea could not be updated.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Edit idea"
      onClose={onClose}
      actions={
        <>
          <button className="button secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="button primary" type="button" disabled={saving || !title.trim()} onClick={() => void save()}>
            {saving ? <LoaderCircle className="spin" size={16} /> : null}
            Save
          </button>
        </>
      }
    >
      <Field label="Channel">
        <select value={channelId} onChange={(event) => setChannelId(event.target.value)}>
          {channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.name}</option>)}
        </select>
      </Field>
      <Field label="Title">
        <input value={title} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field label="Context" hint="Optional">
        <textarea rows={6} value={context} onChange={(event) => setContext(event.target.value)} />
      </Field>
      <Field label="Suggested duration" hint="Optional">
        <input
          type="number"
          min={1}
          max={180}
          step={1}
          value={suggestedDuration}
          onChange={(event) => setSuggestedDuration(event.target.value)}
        />
      </Field>
      {error ? <p className="form-alert" role="alert">{error}</p> : null}
    </Dialog>
  );
}

function ApproveIdeaDialog({
  idea,
  catalog,
  client,
  allowNoPublicationFixture,
  onClose,
  onApproved,
}: {
  idea: BoardItem;
  catalog: Awaited<ReturnType<PortalApiClient["catalog"]["get"]>>;
  client: PortalApiClient;
  allowNoPublicationFixture: boolean;
  onClose(): void;
  onApproved(): void;
}) {
  const channel = catalog.channels.find((item) => item.id === idea.channelId);
  const [profileId, setProfileId] = useState(channel?.defaultProfileId ?? "");
  const [voiceId, setVoiceId] = useState(channel?.defaultVoiceId ?? "");
  const [durationOverride, setDurationOverride] = useState("");
  const [noPublicationFixture, setNoPublicationFixture] = useState(false);
  const [confirmingSuggestion, setConfirmingSuggestion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsedOverride = /^\d+$/u.test(durationOverride.trim()) ? Number(durationOverride) : null;
  const effectiveDuration = parsedOverride ?? idea.suggestedDurationMinutes;
  const targets = effectiveDuration ? calculateWordTargets(effectiveDuration) : null;

  async function submitApproval(
    durationMinutes: number,
    durationSource: "suggested" | "manager_override",
  ) {
    setSaving(true);
    setError(null);
    try {
      await client.ideas.approve(idea.id, {
        profileId,
        voiceId,
        durationMinutes,
        durationSource,
        publicationMode: noPublicationFixture ? "disabled_fixture" : "normal",
      });
      onApproved();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The idea could not be approved.");
      setConfirmingSuggestion(false);
    } finally {
      setSaving(false);
    }
  }

  async function approve() {
    if (!profileId || !voiceId) {
      setError("Choose a profile and voice.");
      return;
    }
    if (durationOverride.trim()) {
      let durationMinutes: number | null;
      try {
        durationMinutes = parseSuggestedDuration(durationOverride);
      } catch (durationError) {
        setError(durationError instanceof Error ? durationError.message : "Duration is invalid.");
        return;
      }
      if (durationMinutes === null) return;
      await submitApproval(durationMinutes, "manager_override");
      return;
    }
    if (idea.suggestedDurationMinutes === null) {
      setError("Choose a duration before adding this job to production.");
      return;
    }
    setError(null);
    setConfirmingSuggestion(true);
  }

  if (confirmingSuggestion && idea.suggestedDurationMinutes !== null) {
    return (
      <Dialog
        title="Use suggested duration?"
        onClose={onClose}
        actions={
          <>
            <button className="button secondary" type="button" disabled={saving} onClick={() => setConfirmingSuggestion(false)}>
              Go back
            </button>
            <button className="button primary" type="button" disabled={saving} onClick={() => void submitApproval(idea.suggestedDurationMinutes!, "suggested")}>
              {saving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
              Use {idea.suggestedDurationMinutes} minutes
            </button>
          </>
        }
      >
        <p>
          No duration override was selected. The suggested duration of <strong>{idea.suggestedDurationMinutes} minutes</strong> will be used.
        </p>
        {error ? <p className="form-alert" role="alert">{error}</p> : null}
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Approve idea"
      onClose={onClose}
      actions={
        <>
          <button className="button secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="button primary" type="button" disabled={saving} onClick={() => void approve()}>
            {saving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
            Approve
          </button>
        </>
      }
    >
      <p><strong>{idea.title}</strong></p>
      {idea.suggestedDurationMinutes ? (
        <p>Suggested duration: <strong>{idea.suggestedDurationMinutes} minutes</strong></p>
      ) : (
        <p>No duration was suggested. Choose one before approval.</p>
      )}
      <Field label="Script profile">
        <select value={profileId} onChange={(event) => setProfileId(event.target.value)}>
          <option value="">Choose a profile</option>
          {catalog.profiles.filter((profile) => profile.active).map((profile) => (
            <option key={profile.id} value={profile.id}>{profile.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Voice">
        <select value={voiceId} onChange={(event) => setVoiceId(event.target.value)}>
          <option value="">Choose a voice</option>
          {catalog.voices.filter((voice) => voice.active).map((voice) => (
            <option key={voice.id} value={voice.id}>{voice.name}</option>
          ))}
        </select>
      </Field>
      <Field label="Duration override" hint={targets ? formatWordRange(targets) : "Required when no duration was suggested."}>
        <input
          type="number"
          min={1}
          max={180}
          step={1}
          value={durationOverride}
          onChange={(event) => setDurationOverride(event.target.value)}
        />
      </Field>
      {allowNoPublicationFixture ? (
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={noPublicationFixture}
            onChange={(event) => setNoPublicationFixture(event.target.checked)}
          />
          <span>
            <strong>No-publication fixture</strong>
            <small>Render and verify locally without Drive or Trello.</small>
          </span>
        </label>
      ) : null}
      {error ? <p className="form-alert" role="alert">{error}</p> : null}
    </Dialog>
  );
}
